<template>
  <div class="user-assets-page">
    <div class="asset-groups-header">
      <div>
        <h3 class="mb-1">
          {{ $t('aside.menu.pages.userAssets') }}
        </h3>
        <div class="text-muted small">
          {{ $t('components.userAssets.subtitle') }}
        </div>
      </div>
      <div>
        <button
          v-if="canCreate"
          class="btn btn-primary"
          @click="openCreateModal"
        >
          <i class="bi bi-plus-lg me-2" />
          {{ $t('components.userAssets.addButton') }}
        </button>
      </div>
    </div>

    <div class="tab-content mt-4">
      <div
        v-if="listError"
        class="alert alert-danger d-flex align-items-center gap-2 mb-3"
      >
        <i class="bi bi-exclamation-triangle-fill" />
        <span>{{ listError }}</span>
      </div>
      <div
        v-if="listLoading"
        class="d-flex align-items-center gap-3 py-3"
      >
        <span
          class="spinner-border text-primary"
          role="status"
        />
        <span>{{ $t('components.userAssets.loading') }}</span>
      </div>
      <div class="data-grid-card">
        <DxDataGrid
          ref="gridRef"
          class="ag-grid"
          :data-source="dataSource"
          :remote-operations="true"
          :hover-state-enabled="true"
          :column-auto-width="false"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-hiding-enabled="true"
          :word-wrap-enabled="true"
        >
          <DxHeaderFilter :visible="true" />
          <DxFilterRow :visible="true" />
          <DxPaging :page-size="20" />
          <DxPager
            :allowed-page-sizes="[10, 20, 50]"
            :show-info="true"
            :show-page-size-selector="true"
            :show-navigation-buttons="true"
          />

          <DxColumn
            data-field="id"
            :caption="$t('components.userAssets.columns.id')"
            width="80"
            alignment="center"
          />
          <DxColumn
            data-field="email"
            :caption="$t('components.userAssets.columns.email')"
            :min-width="200"
          />
          <DxColumn
            data-field="name"
            :caption="$t('components.userAssets.columns.name')"
            :min-width="150"
          />
          <DxColumn
            data-field="surname"
            :caption="$t('components.userAssets.columns.surname')"
            :min-width="150"
          />
          <DxColumn
            data-field="phone"
            :caption="$t('components.userAssets.columns.phone')"
            :min-width="140"
            :hiding-priority="2"
          />
          <DxColumn
            data-field="roles"
            :caption="$t('components.userAssets.columns.roles')"
            :min-width="180"
            :allow-filtering="false"
            :hiding-priority="1"
            cell-template="roles-template"
          />
          <DxColumn
            data-field="active"
            :caption="$t('components.userAssets.columns.active')"
            alignment="center"
            width="100"
            cell-template="active-template"
          />
          <DxColumn
            :caption="$t('components.assetGroups.columns.actions')"
            :allow-filtering="false"
            :allow-sorting="false"
            width="120"
            alignment="center"
            cell-template="actions-template"
          />

          <template #roles-template="{ data }">
            <div class="d-flex gap-1 flex-wrap">
              <span
                class="badge"
                :class="{
                  'bg-success': data.data.roles.includes('ROLE_ASSET_OWNER'),
                  'bg-primary': data.data.roles.includes('ROLE_ASSET_ADMIN') && !data.data.roles.includes('ROLE_ASSET_OWNER'),
                  'bg-info': data.data.roles.includes('ROLE_USER') && data.data.roles.includes('ROLE_ASSET') && !data.data.roles.includes('ROLE_ASSET_OWNER')
                }"
              >
                {{ getRoleLabel(data.data.roles) }}
              </span>
            </div>
          </template>

          <template #active-template="{ data }">
            <i
              class="bi"
              :class="data.data.active ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
            />
          </template>

          <template #actions-template="{ data }">
            <div class="d-flex gap-2 justify-content-center">
              <button
                v-if="!isReadOnly"
                type="button"
                class="btn btn-sm btn-outline-primary"
                :title="$t('components.assetGroups.actions.edit')"
                @click="openEditModal(data.data)"
              >
                <i class="bi bi-pencil" />
              </button>
              <button
                v-if="canEdit && !data.data.roles.includes('ROLE_ASSET_OWNER')"
                type="button"
                class="btn btn-sm btn-outline-danger"
                :title="$t('components.userAssets.actions.delete')"
                @click="confirmDelete(data.data)"
              >
                <i class="bi bi-trash" />
              </button>
              <button
                v-if="isReadOnly"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :title="$t('components.assetGroups.actions.view')"
                @click="openViewModal(data.data)"
              >
                <i class="bi bi-eye" />
              </button>
            </div>
          </template>
        </DxDataGrid>
        <div
          v-if="isGridEmpty"
          class="text-center text-muted py-3"
        >
          {{ $t('components.userAssets.empty') }}
        </div>
      </div>
    </div>

    <!-- User Asset Form Modal -->
    <UserAssetFormModal
      ref="userAssetModalRef"
      @saved="handleUserSaved"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuasar } from 'quasar'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager
} from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'
import { getUserAssets, deleteUserAsset } from '@/crud/user-asset'
import UserAssetFormModal from '@/components/content/_modals/UserAssetFormModal.vue'
import { canCreateAssetGroups, canEditAssetGroups, isRestrictedAssetUser } from '@/utils/tools'

export default defineComponent({
  name: 'UserAssetsView',
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    UserAssetFormModal
  },
  emits: ['pageName'],
  setup(props, context) {
    const { t, locale } = useI18n()
    const vm = getCurrentInstance()
    const toast = vm?.proxy?.$toast
    const quasar = useQuasar()
    const listLoading = ref(false)
    const listError = ref<string | null>(null)
    const gridRef = ref()
    const userAssetModalRef = ref()

    const isGridEmpty = computed(() => {
      const inst = gridRef.value?.instance
      return !listLoading.value && !listError.value && inst && inst.totalCount() === 0
    })

    // Permission checks
    const canCreate = computed(() => canCreateAssetGroups())
    const canEdit = computed(() => canEditAssetGroups())
    const isReadOnly = computed(() => isRestrictedAssetUser())

    const setTitle = () => context.emit('pageName', t('aside.menu.pages.userAssets'))
    setTitle()
    watch(locale, setTitle)

    const buildSearchValue = (filter: any): string => {
      if (!filter) return ''

      const extract = (f: any): string => {
        if (Array.isArray(f) && f.length >= 3 && typeof f[0] === 'string') {
          return f[2] || ''
        }
        return ''
      }

      const gather = (f: any): string => {
        if (!f) return ''
        if (typeof f[0] === 'string') {
          return extract(f)
        }
        if (Array.isArray(f)) {
          for (const item of f) {
            const val = gather(item)
            if (val) return val
          }
        }
        return ''
      }

      return gather(filter)
    }

    const dataSource = new CustomStore({
      cacheRawData: false,
      key: 'id',
      load: async (loadOptions) => {
        const params: any = {
          skip: loadOptions.skip ?? 0,
          take: loadOptions.take ?? 20
        }

        const searchValue = buildSearchValue(loadOptions.filter)
        if (searchValue) params.searchValue = searchValue

        if (loadOptions.sort) {
          params.sort = JSON.stringify(loadOptions.sort)
        }
        if (loadOptions.filter) {
          params.filter = JSON.stringify(loadOptions.filter)
        }

        try {
          listLoading.value = true
          listError.value = null

          const response = await getUserAssets(params)
          const payload = response.data?.data || {}
          const rawRows: any[] = Array.isArray(payload) ? payload : (payload.result || payload.data || [])

          const rows = (rawRows || []).map((u: any) => ({
            id: u.id,
            email: u.email,
            name: u.name || '',
            surname: u.surname || '',
            phone: u.phone || '',
            roles: u.roles || [],
            active: u.active ?? true,
            company: u.company
          }))

          const total = response.data?.totalCount ?? payload.count ?? 0
          return { data: rows, totalCount: total }
        } catch (e: any) {
          listError.value = e?.response?.data?.error || e?.message || t('components.userAssets.error')
          throw e
        } finally {
          listLoading.value = false
        }
      }
    })

    const formatRole = (role: string): string => {
      const roleMap: Record<string, string> = {
        'ROLE_ASSET_ADMIN': 'Asset Admin',
        'ROLE_ASSET': 'Asset',
        'ROLE_ADMIN': 'Admin',
        'ROLE_BOSS': 'Boss',
        'ROLE_USER': 'User',
        'ROLE_ASSET_OWNER': 'Owner'
      }
      return roleMap[role] || role.replace('ROLE_', '')
    }

    // Get role label based on user's roles
    const getRoleLabel = (roles: string[]): string => {
      if (!roles || roles.length === 0) return ''

      // ROLE_ASSET_OWNER → "Właściciel assetów"
      if (roles.includes('ROLE_ASSET_OWNER')) {
        return t('roleLabels.assetOwner')
      }

      // ROLE_ASSET_ADMIN + ROLE_ASSET → "Administrator assetów"
      if (roles.includes('ROLE_ASSET_ADMIN') && roles.includes('ROLE_ASSET')) {
        return t('roleLabels.assetAdmin')
      }

      // ROLE_USER + ROLE_ASSET → "Użytkownik assetów"
      if (roles.includes('ROLE_USER') && roles.includes('ROLE_ASSET')) {
        return t('roleLabels.assetUser')
      }

      // Default: show all roles
      return roles.map(r => formatRole(r)).join(', ')
    }

    const openCreateModal = () => {
      userAssetModalRef.value?.openModal()
    }

    const openEditModal = (user: any) => {
      userAssetModalRef.value?.openModal(user)
    }

    const openViewModal = (user: any) => {
      userAssetModalRef.value?.openModal(user, true) // Pass true for read-only mode
    }

    const confirmDelete = async (user: any) => {
      quasar.dialog({
        title: t('components.userAssets.actions.deleteTitle'),
        message: t('components.userAssets.actions.confirmDelete', { name: `${user.name} ${user.surname}` }),
        cancel: true
      }).onOk(async () => {
        try {
          await deleteUserAsset(user.id, user.email)
          toast?.success(t('components.userAssets.actions.deleteSuccess'))
          gridRef.value?.instance?.refresh()
        } catch (e: any) {
          const errorMsg = e?.response?.data?.error || e?.message || t('components.userAssets.actions.deleteError')
          listError.value = errorMsg
          toast?.error(errorMsg)
        }
      })
    }

    const handleUserSaved = () => {
      // Refresh grid
      gridRef.value?.instance.refresh()
      toast?.success(t('components.userAssets.modal.success'))
    }

    return {
      listLoading,
      listError,
      gridRef,
      userAssetModalRef,
      dataSource,
      isGridEmpty,
      canCreate,
      canEdit,
      isReadOnly,
      formatRole,
      getRoleLabel,
      openCreateModal,
      openEditModal,
      openViewModal,
      confirmDelete,
      handleUserSaved
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-groups-view.scss';

.user-assets-page {
  .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>
